import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  AlertComponent,
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ContainerComponent,
  FormControlDirective,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalTitleDirective,
  PageItemDirective,
  PageLinkDirective,
  PaginationComponent,
  SpinnerComponent,
  TableDirective
} from '@coreui/angular-pro';
import { BankReconciliationService } from 'app';
import {
  BankAccountInfo,
  BankBooking,
  BankReconciliationStatus,
  BankStatementImportRecord,
  BankSuggestion,
  BankTransactionFilters,
  BankTransactionRecord,
  Logger
} from 'shared';

/**
 * Admin: import bank-statement CSVs and reconcile incoming transfers against open
 * bank-transfer bookings.
 *
 * Lives in libs/credo and is routed from each app's admin.routes.ts (like
 * LegalAcceptancesPageComponent) — the flow is identical across brands; only the
 * per-domain bank account differs, and the backend resolves that from the request.
 *
 * Matching only LINKS a transaction to an order; the separate "Book" action runs
 * the confirmation that moves money.
 */
@Component({
  selector: 'app-bank-reconciliation-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AlertComponent,
    BadgeComponent,
    ButtonDirective,
    CardComponent,
    CardBodyComponent,
    CardHeaderComponent,
    ContainerComponent,
    FormControlDirective,
    ModalComponent,
    ModalHeaderComponent,
    ModalTitleDirective,
    ModalBodyComponent,
    ModalFooterComponent,
    PaginationComponent,
    PageItemDirective,
    PageLinkDirective,
    SpinnerComponent,
    TableDirective
  ],
  templateUrl: './bank-reconciliation-page.component.html',
  styleUrls: ['./bank-reconciliation-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankReconciliationPageComponent {
  private service = inject(BankReconciliationService);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('BankReconciliationPageComponent');

  protected readonly statuses: (BankReconciliationStatus | '')[] = ['', 'unmatched', 'matched', 'booked', 'ignored'];

  protected account = signal<BankAccountInfo | null>(null);
  protected noAccount = signal(false);
  protected imports = signal<BankStatementImportRecord[]>([]);
  protected transactions = signal<BankTransactionRecord[]>([]);
  protected pagination = signal({ page: 1, per_page: 50, total: 0, total_pages: 0 });

  protected loading = signal(true);
  protected uploadBusy = signal(false);
  protected busyId = signal<number | null>(null);
  protected error = signal<string | null>(null);
  protected lastImport = signal<BankStatementImportRecord | null>(null);

  protected filters: BankTransactionFilters = { status: '', q: '', page: 1, per_page: 50 };

  // Match modal
  protected matchOpen = signal(false);
  protected matchTxn = signal<BankTransactionRecord | null>(null);
  protected matchLoading = signal(false);
  protected suggestions = signal<BankSuggestion[]>([]);
  protected openBookings = signal<BankBooking[]>([]);

  protected selectedFile: File | null = null;

  constructor() {
    this.bootstrap();
  }

  private bootstrap(): void {
    this.service.getAccount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        if (!res.account) {
          this.noAccount.set(true);
          this.loading.set(false);
          return;
        }
        this.account.set(res.account);
        this.loadImports();
        this.load();
      },
      error: err => {
        this.logger.error('Failed to load bank account', err);
        this.error.set('load');
        this.loading.set(false);
      }
    });
  }

  protected load(): void {
    this.loading.set(true);
    this.service.getTransactions(this.filters).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.transactions.set(res.transactions);
        this.pagination.set(res.pagination);
        this.loading.set(false);
      },
      error: err => {
        this.logger.error('Failed to load transactions', err);
        this.error.set('load');
        this.loading.set(false);
      }
    });
  }

  private loadImports(): void {
    this.service.getImports().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => this.imports.set(res.imports),
      error: err => this.logger.error('Failed to load imports', err)
    });
  }

  // ---- upload ----
  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  protected upload(): void {
    if (!this.selectedFile || this.uploadBusy()) {
      return;
    }
    this.uploadBusy.set(true);
    this.error.set(null);
    this.service.uploadCsv(this.selectedFile).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.lastImport.set(res.import);
        this.uploadBusy.set(false);
        this.selectedFile = null;
        this.filters = { ...this.filters, page: 1 };
        this.loadImports();
        this.load();
      },
      error: err => {
        this.logger.error('CSV upload failed', err);
        this.error.set(err?.error?.error || 'upload');
        this.uploadBusy.set(false);
      }
    });
  }

  // ---- filters / paging ----
  protected setStatus(status: BankReconciliationStatus | ''): void {
    this.filters = { ...this.filters, status, page: 1 };
    this.load();
  }

  protected search(): void {
    this.filters = { ...this.filters, page: 1 };
    this.load();
  }

  protected goToPage(page: number): void {
    if (page < 1 || page > this.pagination().total_pages) {
      return;
    }
    this.filters = { ...this.filters, page };
    this.load();
  }

  protected pages(): number[] {
    const { page, total_pages } = this.pagination();
    const start = Math.max(1, Math.min(page - 3, total_pages - 6));
    const end = Math.min(total_pages, start + 6);
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);
  }

  // ---- match modal ----
  protected openMatch(txn: BankTransactionRecord): void {
    this.matchTxn.set(txn);
    this.matchOpen.set(true);
    this.matchLoading.set(true);
    this.suggestions.set([]);

    this.service.getSuggestions(txn.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => {
        this.suggestions.set(res.suggestions);
        this.matchLoading.set(false);
      },
      error: err => {
        this.logger.error('Failed to load suggestions', err);
        this.matchLoading.set(false);
      }
    });

    this.service.getOpenBookings().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: res => this.openBookings.set(res.open_bookings),
      error: err => this.logger.error('Failed to load open bookings', err)
    });
  }

  protected closeMatch(): void {
    this.matchOpen.set(false);
    this.matchTxn.set(null);
    this.suggestions.set([]);
  }

  protected onMatchVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeMatch();
    }
  }

  protected matchTo(orderId: number): void {
    const txn = this.matchTxn();
    if (!txn) {
      return;
    }
    this.busyId.set(txn.id);
    this.service.match(txn.id, orderId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.busyId.set(null);
        this.closeMatch();
        this.load();
      },
      error: err => {
        this.logger.error('Match failed', err);
        this.error.set(err?.error?.error || 'action');
        this.busyId.set(null);
      }
    });
  }

  // ---- row actions ----
  protected book(txn: BankTransactionRecord): void {
    const amount = `${txn.amount} ${txn.currency}`;
    if (!confirm(`Book this transfer (${amount}) and confirm the payment? This completes the order and notifies the member.`)) {
      return;
    }
    this.runAction(txn, this.service.book(txn.id));
  }

  protected unmatch(txn: BankTransactionRecord): void {
    this.runAction(txn, this.service.unmatch(txn.id));
  }

  protected ignore(txn: BankTransactionRecord): void {
    this.runAction(txn, this.service.ignore(txn.id));
  }

  protected unignore(txn: BankTransactionRecord): void {
    this.runAction(txn, this.service.unignore(txn.id));
  }

  private runAction(txn: BankTransactionRecord, obs: ReturnType<BankReconciliationService['book']>): void {
    this.busyId.set(txn.id);
    this.error.set(null);
    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.busyId.set(null);
        this.load();
      },
      error: err => {
        this.logger.error('Action failed', err);
        this.error.set(err?.error?.error || 'action');
        this.busyId.set(null);
      }
    });
  }

  // ---- view helpers ----
  protected statusColor(status: BankReconciliationStatus): string {
    switch (status) {
      case 'booked': return 'success';
      case 'matched': return 'info';
      case 'ignored': return 'secondary';
      default: return 'warning';
    }
  }
}
